'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { ScoreStatistics } from '@/lib/api';

interface NormalDistributionTooltipProps {
    stats: ScoreStatistics;
    userScore: number | null;
}

export function NormalDistributionTooltip({ stats, userScore }: NormalDistributionTooltipProps) {
    const t = useTranslations('certification.comparison');

    // Calculate histogram bins for the distribution
    const histogramData = useMemo(() => {
        if (!stats.distribution || stats.distribution.length === 0) {
            return [];
        }

        // Create 10 bins from 0 to 5
        const numBins = 10;
        const binWidth = 5 / numBins;
        const bins = Array(numBins).fill(0);

        stats.distribution.forEach(score => {
            const binIndex = Math.min(Math.floor(score / binWidth), numBins - 1);
            bins[binIndex]++;
        });

        const maxCount = Math.max(...bins, 1);
        return bins.map((count, i) => ({
            x: i * binWidth + binWidth / 2,
            count,
            height: (count / maxCount) * 100,
        }));
    }, [stats.distribution]);

    // Calculate position of user score marker
    const userScorePosition = useMemo(() => {
        if (userScore === null) return null;
        return (userScore / 5) * 100; // Convert to percentage of width
    }, [userScore]);

    // Chart dimensions
    const chartWidth = 200;
    const chartHeight = 80;
    const padding = { top: 10, right: 10, bottom: 20, left: 10 };
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    return (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[99999] hidden group-hover:block">
            <div className="bg-white/80 backdrop-blur-md rounded-none shadow-xl border border-white/20 p-3 min-w-[220px]">
                {/* Arrow */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-white/80 backdrop-blur-md" />
                {/* Border Arrow for outline */}
                <div className="absolute -top-[9px] left-1/2 -translate-x-1/2 w-0 h-0 z-[-1] border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent border-b-gray-200" />

                {/* Title */}
                <div className="text-xs text-black mb-2 text-center font-medium">
                    {t('distribution')} ({stats.count} {t('certifications')})
                </div>

                {/* Chart */}
                <svg width={chartWidth} height={chartHeight} className="block mx-auto">
                    {/* Background */}
                    <rect
                        x={padding.left}
                        y={padding.top}
                        width={innerWidth}
                        height={innerHeight}
                        fill="#f1f5f9"
                        rx={0}
                    />

                    {/* Histogram bars */}
                    {histogramData.map((bin, i) => {
                        const barWidth = innerWidth / histogramData.length - 2;
                        const barHeight = (bin.height / 100) * innerHeight;
                        const x = padding.left + (i / histogramData.length) * innerWidth + 1;
                        const y = padding.top + innerHeight - barHeight;

                        return (
                            <rect
                                key={i}
                                x={x}
                                y={y}
                                width={barWidth}
                                height={barHeight}
                                fill="#8b5cf6"
                                rx={0}
                            />
                        );
                    })}

                    {/* Mean line */}
                    <line
                        x1={padding.left + (stats.mean / 5) * innerWidth}
                        y1={padding.top}
                        x2={padding.left + (stats.mean / 5) * innerWidth}
                        y2={padding.top + innerHeight}
                        stroke="#22c55e"
                        strokeWidth={2}
                        strokeDasharray="4,2"
                    />

                    {/* User score marker */}
                    {userScorePosition !== null && (
                        <g>
                            <line
                                x1={padding.left + (userScorePosition / 100) * innerWidth}
                                y1={padding.top}
                                x2={padding.left + (userScorePosition / 100) * innerWidth}
                                y2={padding.top + innerHeight}
                                stroke="#f97316"
                                strokeWidth={2}
                            />
                            <circle
                                cx={padding.left + (userScorePosition / 100) * innerWidth}
                                cy={padding.top + 5}
                                r={4}
                                fill="#f97316"
                            />
                        </g>
                    )}

                    {/* X-axis labels */}
                    {[0, 1, 2, 3, 4, 5].map(val => (
                        <text
                            key={val}
                            x={padding.left + (val / 5) * innerWidth}
                            y={chartHeight - 4}
                            fontSize={9}
                            fill="#64748b"
                            textAnchor="middle"
                        >
                            {val}
                        </text>
                    ))}
                </svg>

                {/* Legend */}
                <div className="flex justify-center gap-4 mt-2 text-[10px]">
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-0.5 bg-emerald-500" style={{ borderStyle: 'dashed', borderWidth: '1px 0' }} />
                        <span className="text-gray-500">{t('avgLabel')}: {stats.mean.toFixed(1)}</span>
                    </div>
                    {userScore !== null && (
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-none bg-orange-500" />
                            <span className="text-gray-500">{t('yourScore')}: {userScore.toFixed(1)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
